import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  Close,
  Download,
  ExpandLess,
  ExpandMore,
  Info,
  Upload,
} from '@mui/icons-material';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import FileButton from 'src/components/FileButton';
import { _useLcaProduct } from '../hooks';

interface ImportError {
  type: 'validation' | 'format' | 'system' | 'legacy';
  title: string;
  message: string;
  details?: string[];
  severity: 'error' | 'warning' | 'info';
}

interface ImportSuccess {
  status: string;
  filename: string;
  template_version: string;
  product_id: number;
  product_name: string;
  legacy_compatibility?: {
    applied: boolean;
    handled_fields: string[];
    message: string;
  };
}

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

const SUPPORTED_TEMPLATE_SHEETS = [
  'LcaProduct',
  'LcaProcess',
  'LcaObject',
  'LcaProductMaterial',
  'LcaObjectCustomFactor',
  'LcaSubProduct',
  'LcaRegion',
  'LcaProductGoals',
];

export default function ProductExcelUploadDialog(props: DialogProps) {
  const { t } = useTranslation('lca');
  const navigate = useNavigate();
  const [file, setFile] = useState<File | undefined>();
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [successData, setSuccessData] = useState<ImportSuccess | null>(null);
  const [expandedErrors, setExpandedErrors] = useState<number[]>([]);

  const { createFromExcel } = _useLcaProduct();

  const resetState = (clearFile = true) => {
    if (clearFile) {
      setFile(undefined);
    }
    setUploadState('idle');
    setErrors([]);
    setSuccessData(null);
    setExpandedErrors([]);
  };

  const _onClose = () => {
    setTimeout(() => {
      resetState();
    }, 200);
    props?.onClose?.({} as any, 'backdropClick');
  };

  const parseErrorMessage = (errorResponse: AxiosError): ImportError[] => {
    const nextErrors: ImportError[] = [];

    try {
      const detail =
        (errorResponse.response?.data as any)?.detail || errorResponse.message;

      const djangoValidationMatch = detail.match(
        /(\w+): \[ValidationError\(\[['"]([^'"]+)['"]]\)]/
      );
      if (djangoValidationMatch) {
        const [, fieldName, errorMsg] = djangoValidationMatch;

        if (
          fieldName === 'ce_mark' ||
          errorMsg.includes('value must be either True or False')
        ) {
          nextErrors.push({
            type: 'validation',
            title: 'Boolean Field Error',
            message: `The field "${fieldName}" has an invalid value.`,
            details: [
              'Boolean fields should contain "true", "false", "yes", "no", or be left empty',
              'Do not use "None", "null", "N/A" or other text in boolean fields',
              'If unsure, leave the field completely blank',
              `Technical error: ${errorMsg}`,
            ],
            severity: 'error',
          });
        } else if (
          errorMsg.includes('not a valid float') ||
          errorMsg.includes('not a valid integer')
        ) {
          nextErrors.push({
            type: 'validation',
            title: 'Numeric Field Error',
            message: `The field "${fieldName}" contains an invalid number.`,
            details: [
              'Ensure the field contains only numbers (e.g., 12.5, 100)',
              'Remove any letters, symbols, or formatting',
              'Use decimal point (.) for decimal numbers, not comma (,)',
              'Leave empty if no value is needed',
              `Technical error: ${errorMsg}`,
            ],
            severity: 'error',
          });
        } else {
          nextErrors.push({
            type: 'validation',
            title: 'Field Validation Error',
            message: `The field "${fieldName}" has an invalid value.`,
            details: [
              'Please check the value in this field',
              'Make sure it matches the expected format',
              `Technical error: ${errorMsg}`,
            ],
            severity: 'error',
          });
        }
      } else if (detail.includes('Validation errors in')) {
        const match = detail.match(/Validation errors in ([^:]+): (.*)/);
        if (match) {
          const [, category, errorDetails] = match;
          const detailLines = errorDetails
            .split(';')
            .map((line: string) => line.trim());

          nextErrors.push({
            type: 'validation',
            title: `${category} Validation Errors`,
            message: `Issues found in ${category.toLowerCase()} data:`,
            details: detailLines,
            severity: 'error',
          });
        } else {
          nextErrors.push({
            type: 'validation',
            title: 'Data Validation Errors',
            message: detail,
            severity: 'error',
          });
        }
      } else if (detail.includes('Invalid numeric values in product data')) {
        nextErrors.push({
          type: 'format',
          title: 'Numeric Data Format Error',
          message: 'Some numeric fields contain invalid values.',
          details: [
            'Please ensure weight, volume, and other numeric fields contain valid numbers',
            'Empty fields should be left blank or contain "-", "N/A", etc.',
            'Remove any text, special characters, or formatting from numeric fields',
          ],
          severity: 'error',
        });
      } else if (
        detail.includes('Invalid CPC industry code') ||
        detail.includes('Invalid GPC code') ||
        detail.includes('Invalid NACE code')
      ) {
        const codeMatch = detail.match(/Invalid ([^:]+): '([^']+)'/);
        const codeType = codeMatch ? codeMatch[1] : 'reference code';
        const codeValue = codeMatch ? codeMatch[2] : 'unknown';

        nextErrors.push({
          type: 'validation',
          title: 'Invalid Reference Data',
          message: `The ${codeType.toLowerCase()} "${codeValue}" is not valid.`,
          details: [
            'Please select values from the dropdown options provided in the Excel template',
            "Make sure you haven't manually typed values instead of selecting from dropdowns",
            "If you're using an old template, download the latest version",
          ],
          severity: 'error',
        });
      } else if (detail.includes('Missing product data in template')) {
        nextErrors.push({
          type: 'format',
          title: 'Template Structure Error',
          message: 'The Excel template appears to be missing required data.',
          details: [
            'Ensure the LcaProduct sheet contains data',
            "Check that you haven't accidentally deleted rows or columns",
            'Download a fresh template if the structure seems corrupted',
          ],
          severity: 'error',
        });
      } else if (detail.includes('Missing product ID in template')) {
        nextErrors.push({
          type: 'format',
          title: 'Template Corruption',
          message: 'Cannot determine the source product from the template.',
          details: [
            'The template may have been modified incorrectly',
            'Please export a fresh template and try again',
            'Do not modify the ID columns in the template',
          ],
          severity: 'error',
        });
      } else if (
        detail.includes('value must be either True or False') ||
        detail.includes('ce_mark:')
      ) {
        nextErrors.push({
          type: 'validation',
          title: 'Boolean Field Error',
          message: 'Some yes/no fields contain invalid values.',
          details: [
            'Boolean fields like "CE Mark" should contain "true", "false", "yes", "no", or be left empty',
            'Do not use "None", "null", "N/A" or other text in boolean fields',
            'Use the dropdown options provided in the Excel template',
            'If unsure, leave the field empty rather than typing "None"',
          ],
          severity: 'error',
        });
      } else if (detail.includes('Product validation failed')) {
        nextErrors.push({
          type: 'validation',
          title: 'Product Data Validation',
          message: detail.replace('Product validation failed: ', ''),
          details: [
            'Some required product fields may be missing or invalid',
            'Check that all mandatory fields are filled correctly',
          ],
          severity: 'error',
        });
      } else if (detail.includes('Material validation errors')) {
        const errorDetails = detail.replace('Material validation errors: ', '');
        const detailLines = errorDetails
          .split(';')
          .map((line: string) => line.trim());

        nextErrors.push({
          type: 'validation',
          title: 'Material Data Errors',
          message: 'Issues found in material percentage data:',
          details: detailLines,
          severity: 'error',
        });
      } else if (
        detail.includes('Template version') &&
        detail.includes('not supported')
      ) {
        nextErrors.push({
          type: 'format',
          title: 'Unsupported Template Version',
          message: detail,
          details: [
            'Please download the latest Excel template',
            'Template versions are used to ensure compatibility',
          ],
          severity: 'error',
        });
      } else if (detail.includes('Legacy fields found')) {
        nextErrors.push({
          type: 'legacy',
          title: 'Legacy Template Detected',
          message:
            'Your template contains outdated fields that have been automatically handled.',
          details: [
            'Consider downloading a new template for future use to get the latest fields.',
          ],
          severity: 'warning',
        });
      } else {
        nextErrors.push({
          type: 'system',
          title: 'Import Error',
          message: detail,
          details: ['If this error persists, please contact support.'],
          severity: 'error',
        });
      }
    } catch {
      nextErrors.push({
        type: 'system',
        title: 'Unexpected Error',
        message: 'An unexpected error occurred during import.',
        details: ['Please try again or contact support if the issue persists.'],
        severity: 'error',
      });
    }

    return nextErrors;
  };

  const toggleErrorExpansion = (index: number) => {
    setExpandedErrors((prev) =>
      prev.includes(index)
        ? prev.filter((item) => item !== index)
        : [...prev, index]
    );
  };

  const handleUploadClick = async () => {
    if (!file) return;

    setUploadState('uploading');
    setErrors([]);

    try {
      const response = await createFromExcel.mutateAsync(file);
      setSuccessData(response.data);
      setUploadState('success');
    } catch (error) {
      setErrors(parseErrorMessage(error as AxiosError));
      setUploadState('error');
    }
  };

  const handleOpenProduct = () => {
    if (!successData?.product_id) return;
    _onClose();
    navigate(`/lca/products/${successData.product_id}`);
  };

  const renderIdleContent = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('product.importDialog.description')}
      </Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle>{t('product.importDialog.templateAlertTitle')}</AlertTitle>
        {t('product.importDialog.templateAlertDescription')}
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('product.importDialog.stepsTitle')}
        </Typography>
        <List dense sx={{ pt: 0 }}>
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Download fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t('product.importDialog.steps.exportTitle')}
              secondary={t('product.importDialog.steps.exportDescription')}
            />
          </ListItem>
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Info fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t('product.importDialog.steps.editTitle')}
              secondary={t('product.importDialog.steps.editDescription')}
            />
          </ListItem>
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Upload fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t('product.importDialog.steps.importTitle')}
              secondary={t('product.importDialog.steps.importDescription')}
            />
          </ListItem>
        </List>
      </Paper>

      <Typography variant="subtitle2" gutterBottom>
        {t('product.importDialog.supportedSheetsTitle')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {SUPPORTED_TEMPLATE_SHEETS.map((sheetName) => (
          <Chip
            key={sheetName}
            size="small"
            variant="outlined"
            label={sheetName}
          />
        ))}
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>{t('product.importDialog.editingTipTitle')}</AlertTitle>
        {t('product.importDialog.editingTipDescription')}
      </Alert>

      <Button
        startIcon={<Info />}
        onClick={() => navigate('/lca/help-centre')}
        sx={{ mb: 2 }}
      >
        {t('product.importDialog.openHelpCenter')}
      </Button>

      <Typography variant="subtitle2" gutterBottom>
        {t('product.importDialog.uploadSectionTitle')}
      </Typography>

      <FileButton
        file={file}
        setFile={setFile}
        buttonProps={{
          variant: 'outlined',
          fullWidth: true,
          startIcon: <Upload />,
          sx: { py: 2 },
        }}
      />

      {file && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>{t('product.importDialog.readyTitle')}</AlertTitle>
          {t('product.importDialog.readyDescription', { filename: file.name })}
        </Alert>
      )}
    </Box>
  );

  const renderErrorList = () => (
    <Box sx={{ mt: 2 }}>
      {errors.map((error, index) => (
        <Alert
          key={index}
          severity={error.severity}
          sx={{ mb: 1 }}
          action={
            error.details ? (
              <IconButton
                onClick={() => toggleErrorExpansion(index)}
                size="small"
              >
                {expandedErrors.includes(index) ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )}
              </IconButton>
            ) : undefined
          }
        >
          <AlertTitle>{error.title}</AlertTitle>
          {error.message}
          <Collapse in={expandedErrors.includes(index)}>
            <Box sx={{ mt: 1 }}>
              {error.details && (
                <List dense>
                  {error.details.map((detail, detailIndex) => (
                    <ListItem key={detailIndex} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Info fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={detail} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Collapse>
        </Alert>
      ))}
    </Box>
  );

  const renderSuccessContent = () => (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
      <Typography variant="h6" color="success.main" gutterBottom>
        {t('product.importDialog.successTitle')}
      </Typography>

      {successData && (
        <Paper sx={{ p: 2, mt: 2, backgroundColor: 'success.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('product.importDialog.successDetailsTitle')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                {t('product.importDialog.fields.productName')}
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {successData.product_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                {t('product.importDialog.fields.file')}
              </Typography>
              <Typography variant="body2">{successData.filename}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                {t('product.importDialog.fields.templateVersion')}
              </Typography>
              <Chip
                size="small"
                label={successData.template_version}
                color="primary"
                variant="outlined"
              />
            </Box>
          </Box>
        </Paper>
      )}

      {successData?.legacy_compatibility?.applied && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>{t('product.importDialog.legacyTitle')}</AlertTitle>
          {successData.legacy_compatibility.message ||
            t('product.importDialog.legacyDescription')}
          <br />
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('product.importDialog.legacyFields')}:{' '}
            {successData.legacy_compatibility.handled_fields.join(', ') ||
              t('product.importDialog.none')}
          </Typography>
        </Alert>
      )}
    </Box>
  );

  const renderUploadingContent = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CircularProgress size={48} sx={{ mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {t('product.importDialog.processingTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('product.importDialog.processingDescription')}
      </Typography>
    </Box>
  );

  const getDialogTitle = () => {
    switch (uploadState) {
      case 'success':
        return t('product.importDialog.dialogTitles.success');
      case 'error':
        return t('product.importDialog.dialogTitles.error');
      case 'uploading':
        return t('product.importDialog.dialogTitles.uploading');
      default:
        return t('product.importDialog.dialogTitles.idle');
    }
  };

  return (
    <Dialog {...props} onClose={_onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {getDialogTitle()}
        <IconButton onClick={_onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ minWidth: '500px', minHeight: '200px' }}>
        {uploadState === 'idle' && renderIdleContent()}
        {uploadState === 'uploading' && renderUploadingContent()}
        {uploadState === 'success' && renderSuccessContent()}
        {uploadState === 'error' && (
          <Box>
            <Typography variant="body1" color="error.main" sx={{ mb: 2 }}>
              {t('product.importDialog.errorIntro')}
            </Typography>
            {renderErrorList()}

            <Divider sx={{ my: 2 }} />

            <Alert severity="info">
              <AlertTitle>{t('product.importDialog.helpTitle')}</AlertTitle>
              {t('product.importDialog.helpLine1')}
              <br />
              {t('product.importDialog.helpLine2')}
              <br />
              {t('product.importDialog.helpLine3')}
              <br />
              {t('product.importDialog.helpLine4')}
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {uploadState === 'idle' && (
          <>
            <Button onClick={_onClose}>
              {t('product.importDialog.actions.cancel')}
            </Button>
            <Button
              disabled={!file}
              onClick={handleUploadClick}
              variant="contained"
              startIcon={<Upload />}
            >
              {t('product.importDialog.actions.import')}
            </Button>
          </>
        )}

        {uploadState === 'uploading' && (
          <Button onClick={_onClose} disabled>
            {t('product.importDialog.actions.processing')}
          </Button>
        )}

        {uploadState === 'success' && (
          <>
            <Button onClick={_onClose}>
              {t('product.importDialog.actions.close')}
            </Button>
            <Button
              onClick={handleOpenProduct}
              variant="contained"
              disabled={!successData?.product_id}
            >
              {t('product.importDialog.actions.openProduct')}
            </Button>
          </>
        )}

        {uploadState === 'error' && (
          <>
            <Button onClick={_onClose}>
              {t('product.importDialog.actions.close')}
            </Button>
            <Button onClick={() => resetState(false)} variant="contained">
              {t('product.importDialog.actions.tryAgain')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
